import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogHighlights() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        // Get the 3 most recent blogs
        setBlogs(response.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  if (loading || blogs.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-white" data-testid="blog-highlights-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
        >
          <div>
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Latest Insights
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A]">
              Workspace Ideas & Resources
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#0F172A] font-semibold hover:text-[#F97316] transition-colors"
          >
            View All Articles
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog, index) => (
            <motion.article
              key={blog.id || blog.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${blog.slug}`} className="block">
                <div className="relative overflow-hidden mb-4">
                  <div className="aspect-[16/10]">
                    <img src={blog.image}
                      alt={`${blog.title} - Office furniture insights by Ezeefit Mumbai`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#F97316] text-white text-xs font-mono px-3 py-1 uppercase tracking-wider">
                      {blog.category}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
                  <span>{blog.published_at}</span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {blog.read_time}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-[#F97316] transition-colors line-clamp-2">
                  {blog.title}
                </h3>
                
                <p className="text-[#64748B] text-sm line-clamp-2 mb-4">
                  {blog.excerpt}
                </p>
                
                <span className="inline-flex items-center gap-2 text-[#F97316] font-semibold text-sm group-hover:gap-3 transition-all">
                  Read Article
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
